import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NzTimeInput } from '@/components/shared/nz-datetime-input';

// --- Friendly schedule builder ----------------------------------------------
// The server stores a 5-field cron string (croner). Asking admins to write
// cron is a footgun (`0 6 * * 1-5` vs `0 6 1-5 * *` mean very different
// things), so the UI drives a Frequency + Time + Days picker that emits the
// cron under the hood. Anything that doesn't match a preset falls back to a
// raw cron text input so power users aren't blocked.
//
// Originally lived inline in admin/settings.tsx for the RunSheetSchedule
// form; lifted here so the recurring-dispatch toggle on the Dispatch form
// can reuse the same picker without duplicating the cron parser.

export type Frequency = 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'custom';

export const FREQUENCIES: Array<{ value: Frequency; label: string }> = [
  { value: 'daily',    label: 'Every day' },
  { value: 'weekdays', label: 'Weekdays (Mon–Fri)' },
  { value: 'weekly',   label: 'Specific days of the week' },
  { value: 'monthly',  label: 'Specific day of the month' },
  { value: 'custom',   label: 'Custom (advanced cron)' },
];

// Weekday list ordered Mon → Sun for human display; cron expects 0=Sun.
export const WEEKDAYS: Array<{ value: number; short: string }> = [
  { value: 1, short: 'Mon' },
  { value: 2, short: 'Tue' },
  { value: 3, short: 'Wed' },
  { value: 4, short: 'Thu' },
  { value: 5, short: 'Fri' },
  { value: 6, short: 'Sat' },
  { value: 0, short: 'Sun' },
];

export interface ParsedSchedule {
  frequency: Frequency;
  time: string;        // 'HH:MM'
  days: number[];      // weekly only — cron-numbered (0=Sun)
  dayOfMonth: number;  // monthly only
  custom: string;      // raw cron when frequency === 'custom'
}

export function parseCron(cron: string): ParsedSchedule {
  const fallback: ParsedSchedule = {
    frequency: 'custom', time: '06:00', days: [1, 2, 3, 4, 5], dayOfMonth: 1, custom: cron || '0 6 * * *',
  };
  const parts = (cron || '').trim().split(/\s+/);
  if (parts.length !== 5) return fallback;
  const [min, hr, dom, mon, dow] = parts;
  const mInt = Number(min);
  const hInt = Number(hr);
  if (!Number.isInteger(mInt) || !Number.isInteger(hInt) || mInt < 0 || mInt > 59 || hInt < 0 || hInt > 23) {
    return fallback;
  }
  const time = `${String(hInt).padStart(2, '0')}:${String(mInt).padStart(2, '0')}`;
  if (dom === '*' && mon === '*' && dow === '*') {
    return { frequency: 'daily', time, days: [1, 2, 3, 4, 5], dayOfMonth: 1, custom: cron };
  }
  if (dom === '*' && mon === '*' && (dow === '1-5' || dow === '1,2,3,4,5')) {
    return { frequency: 'weekdays', time, days: [1, 2, 3, 4, 5], dayOfMonth: 1, custom: cron };
  }
  if (dom === '*' && mon === '*' && /^[0-6](,[0-6])*$/.test(dow)) {
    const days = dow.split(',').map(Number).sort((a, b) => a - b);
    return { frequency: 'weekly', time, days, dayOfMonth: 1, custom: cron };
  }
  if (mon === '*' && dow === '*' && /^\d+$/.test(dom)) {
    return { frequency: 'monthly', time, days: [1, 2, 3, 4, 5], dayOfMonth: Number(dom), custom: cron };
  }
  return fallback;
}

export function buildCron(s: ParsedSchedule): string {
  const [hhStr, mmStr] = s.time.split(':');
  const hh = Math.max(0, Math.min(23, Number(hhStr) || 0));
  const mm = Math.max(0, Math.min(59, Number(mmStr) || 0));
  switch (s.frequency) {
    case 'daily':    return `${mm} ${hh} * * *`;
    case 'weekdays': return `${mm} ${hh} * * 1-5`;
    case 'weekly': {
      const days = (s.days.length ? [...s.days] : [1]).sort((a, b) => a - b);
      return `${mm} ${hh} * * ${days.join(',')}`;
    }
    case 'monthly':  return `${mm} ${hh} ${s.dayOfMonth || 1} * *`;
    case 'custom':   return s.custom || `${mm} ${hh} * * *`;
  }
}

export function describeCron(s: ParsedSchedule): string {
  const t = s.time;
  switch (s.frequency) {
    case 'daily':    return `Every day at ${t}`;
    case 'weekdays': return `Every weekday (Mon–Fri) at ${t}`;
    case 'weekly': {
      if (s.days.length === 0) return `Pick at least one day of the week`;
      const ordered = [...s.days].sort((a, b) => {
        // Display order: Mon→Sun (Sun is 0 in cron, push to end).
        const ax = a === 0 ? 7 : a;
        const bx = b === 0 ? 7 : b;
        return ax - bx;
      });
      const names = ordered.map((d) => WEEKDAYS.find((w) => w.value === d)?.short ?? '?').join(', ');
      return `${names} at ${t}`;
    }
    case 'monthly':  return `Day ${s.dayOfMonth || 1} of every month at ${t}`;
    case 'custom':   return `Custom cron: ${s.custom || '—'}`;
  }
}

export function ScheduleBuilder({ cron, onChange, allowCustom = true }: { cron: string; onChange: (cron: string) => void; allowCustom?: boolean }) {
  // Builder owns its parsed state. Driving everything from the cron string
  // alone was lossy: switching from Daily to "Specific days" emits
  // `0 6 * * 1,2,3,4,5`, which round-trips back to "Weekdays" on the next
  // parseCron call — the dropdown would silently snap back. Internal state
  // remembers the user's *intent* (frequency + days + dayOfMonth + custom)
  // even when the resulting cron happens to look like a different preset.
  const [state, setState] = React.useState<ParsedSchedule>(() => parseCron(cron));
  // Re-sync when the parent loads a different schedule into the dialog.
  // `cron` is the upstream value; if it disagrees with what we'd emit from
  // local state, the parent must have just swapped records.
  const lastEmittedRef = React.useRef<string>('');
  React.useEffect(() => {
    if (cron && cron !== lastEmittedRef.current) {
      setState(parseCron(cron));
      lastEmittedRef.current = cron;
    }
  }, [cron]);

  const update = (patch: Partial<ParsedSchedule>) => {
    setState((prev) => {
      let next = { ...prev, ...patch };
      // When switching INTO "Specific days", reset the day list to a single
      // sensible default so the emitted cron (`0 6 * * 1`) doesn't collide
      // with the Weekdays preset (`0 6 * * 1-5`).
      if (patch.frequency === 'weekly' && prev.frequency !== 'weekly') {
        next = { ...next, days: prev.days.length > 0 && prev.days.length !== 5 ? prev.days : [1] };
      }
      // When switching INTO Custom, seed the textarea with whatever cron the
      // current preset was producing — the operator can then edit freely.
      // The internal-state design (no re-parse from prop after our own
      // emit) keeps `frequency = 'custom'` sticky even if the typed cron
      // would otherwise round-trip to a preset.
      if (patch.frequency === 'custom' && prev.frequency !== 'custom') {
        next = { ...next, custom: prev.custom || buildCron(prev) };
      }
      const emitted = buildCron(next);
      lastEmittedRef.current = emitted;
      onChange(emitted);
      return next;
    });
  };
  const parsed = state;
  return (
    <div className="space-y-3 rounded-md border p-3 bg-muted/20">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">How often?</Label>
          <Select value={parsed.frequency} onValueChange={(v) => update({ frequency: v as Frequency })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {FREQUENCIES.filter((f) => allowCustom || f.value !== 'custom').map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {parsed.frequency !== 'custom' && (
          <div>
            <Label className="text-xs">Time of day</Label>
            <NzTimeInput ariaLabel="Time of day" value={parsed.time} onChange={(v) => update({ time: v || '06:00' })} />
          </div>
        )}
      </div>

      {parsed.frequency === 'weekly' && (
        <div>
          <Label className="text-xs">Days of the week</Label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {WEEKDAYS.map((w) => {
              const on = parsed.days.includes(w.value);
              return (
                <button
                  key={w.value}
                  type="button"
                  onClick={() => update({ days: on ? parsed.days.filter((d) => d !== w.value) : [...parsed.days, w.value] })}
                  className={`px-3 py-1 rounded-full text-xs border transition-colors ${on ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent'}`}
                >
                  {w.short}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {parsed.frequency === 'monthly' && (
        <div>
          <Label className="text-xs">Day of the month</Label>
          <Input
            type="number" min={1} max={31}
            value={parsed.dayOfMonth}
            onChange={(e) => update({ dayOfMonth: Math.min(31, Math.max(1, Number(e.target.value) || 1)) })}
            className="w-24"
          />
        </div>
      )}

      {parsed.frequency === 'custom' && allowCustom && (
        <div>
          <Label className="text-xs">Cron expression</Label>
          <Input
            value={parsed.custom}
            onChange={(e) => update({ custom: e.target.value })}
            placeholder="0 6 * * 1-5"
            className="font-mono"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            5 fields: minute · hour · day-of-month · month · day-of-week. E.g. <code>0 6 * * 1-5</code> = 6am Mon–Fri.
          </p>
        </div>
      )}

      <div className="text-xs flex items-baseline gap-2">
        <span className="font-medium text-muted-foreground">When this runs:</span>
        <span>{describeCron(parsed)}</span>
      </div>
    </div>
  );
}
